/**
 * Contract-derived JSDoc typedefs for Professional Contacts / Professional Details.
 *
 * These mirror `openapi/professional-contacts-openapi.json` (v1.2.0) field-for-field.
 * This project is plain JS/JSX (no TypeScript) — per CLAUDE.md §1 we do NOT run
 * `openapi-typescript` here; these typedefs are the hand-derived JS equivalent so editors
 * still get shape hints. Do not add a field here that isn't in the contract — if the UI
 * needs something the contract doesn't have, add it to the contract first (golden rule #1).
 */

/** @typedef {"Solicitor"|"BorrowerSolicitor"|"Broker"|"Valuer"|"Surveyor"|"Administrator"|"Receiver"} FirmType */

/** @typedef {"BankSolicitor"|"BankValuer"|"MonitoringSurveyor"|"Broker"|"BorrowerSolicitor"|"Administrator"|"Receiver"} LoanPartyRole */

/**
 * @typedef {Object} Contact
 * @property {string} [id]
 * @property {string} name
 * @property {string} [role]
 * @property {string} email
 * @property {string} phone
 * @property {boolean} [isMain]
 */

/**
 * @typedef {Object} Address
 * @property {string} [id]
 * @property {string} building
 * @property {string} street
 * @property {string} town
 * @property {string} postcode
 * @property {string} [country] default "United Kingdom"
 * @property {boolean} [isPreferred]
 */

/**
 * @typedef {Object} BankAccount
 * @property {string} [id]
 * @property {string} [beneficiaryName]
 * @property {string} [bankType] Collections Account / Funding Account / Operations Account — sourced from the app's BankType option set, not a contract enum.
 * @property {string} [accountNumber]
 * @property {string} sortCode required once a row is started
 * @property {string} bankName required — creditor bank name
 * @property {string} town required — creditor bank town
 * @property {string} postcode required — creditor bank postcode
 * @property {string} country required — creditor bank country
 * @property {string} address required — creditor bank address; always manual, never auto-filled
 * @property {boolean} [isMain] default false
 */

/**
 * @typedef {Object} DeletedBankAccount
 * @property {string} [beneficiaryName]
 * @property {string} [bankType]
 * @property {string} [accountNumber]
 * @property {string} [sortCode]
 * @property {string} [bankName]
 * @property {string} [town]
 * @property {string} [postcode]
 * @property {string} [country]
 * @property {string} [address]
 * @property {string} [deletedUtc] ISO date-time
 * @property {string} [deletedBy]
 */

/**
 * A document belonging to a firm itself (v1.9.0) — PII certificates, terms of business,
 * panel-approval letters, NDAs. Distinct from a loan's Documents Dashboard; a firm document is
 * never filed against a loan and never appears there. File bytes go through the existing
 * Document Helper Azure Function, exactly like QuoteDocument — this contract carries only the
 * reference.
 * @typedef {Object} FirmDocument
 * @property {string} documentId minted client-side (uuid) — part of the blob path, so it exists before this row does
 * @property {string} fileName ≤255 chars
 * @property {string} filePath Document Helper storage reference: "professional-contacts/{firmId}/{documentId}/{fileName}" — see buildFirmDocumentFilePath
 * @property {string} fileExtension lower-cased, leading dot
 * @property {?string} [contentType] browser-reported, advisory only
 * @property {?number} [sizeBytes] browser-reported, advisory only — the API never sees the bytes, so this is a consistency check, not enforcement
 * @property {string} uploadedBy from the auth context — ignored if sent on create
 * @property {string} uploadedUtc ISO date-time — server-stamped, ignored if sent on create
 */

/**
 * v1.12.0: paged/sorted/searched result of GET .../documents, same items/page/pageSize/total shape
 * as AssociatedLoanPage/FirmSummaryPage.
 * @typedef {Object} FirmDocumentPage
 * @property {FirmDocument[]} items
 * @property {number} page
 * @property {number} pageSize
 * @property {number} total
 */

/**
 * @typedef {Object} DeletedContact
 * @property {string} [name]
 * @property {string} [role]
 * @property {string} [email]
 * @property {string} [phone]
 * @property {string} [deletedUtc] ISO date-time
 * @property {string} [deletedBy]
 */

/**
 * @typedef {Object} DeletedAddress
 * @property {string} [building]
 * @property {string} [street]
 * @property {string} [town]
 * @property {string} [postcode]
 * @property {string} [country]
 * @property {string} [deletedUtc] ISO date-time
 * @property {string} [deletedBy]
 */

/**
 * @typedef {Object} FirmFlags
 * @property {boolean} [active]
 * @property {boolean} [panelApproved]
 * @property {boolean} [distressedOnly]
 * @property {boolean} [noFurtherInstructions] OperationalSupervisor only (DF176)
 */

/**
 * @typedef {Object} FirmUpsert
 * @property {FirmType} type
 * @property {string} name
 * @property {string} mainPhone
 * @property {string} email company email (mandatory, DF163)
 * @property {number} [piiValue]
 * @property {string} [piiExpiry] ISO date (YYYY-MM-DD)
 * @property {boolean} [panelApproved]
 * @property {string} [panelApprovedDate] ISO date
 * @property {boolean} [distressedOnly]
 * @property {boolean} [active] default true
 * @property {boolean} [noFurtherInstructions] default false
 * @property {string} [notes]
 * @property {Contact[]} contacts minItems 1
 * @property {Address[]} addresses minItems 1
 * @property {BankAccount[]} [bankAccounts] no minItems — unlike contacts/addresses, a firm may have zero (confirmed decision, v1.3.0)
 */

/**
 * documentCount (v1.9.0) is a cheap COUNT(*), mirroring associatedLoanCount — absent means "not
 * reported" (e.g. an API running against a database where FIRM-DOCUMENTS-001's SQL script hasn't
 * been applied yet), and the UI must render nothing rather than 0 in that case. `documentCount
 * === 0` is the one value that lets the UI skip the GET .../documents call entirely.
 * @typedef {FirmUpsert & {id: string, createdUtc?: string, modifiedUtc?: string, deletedContacts?: DeletedContact[], deletedAddresses?: DeletedAddress[], deletedBankAccounts?: DeletedBankAccount[], associatedLoanCount?: number, documentCount?: number}} Firm
 */

/**
 * @typedef {Object} FirmSummary
 * @property {string} [id]
 * @property {FirmType} [type]
 * @property {string} [name]
 * @property {string} [mainContactName]
 * @property {string} [email]
 * @property {number} [contactCount]
 * @property {number} [addressCount]
 * @property {"Valid"|"Expiring"|"None"} [piiStatus]
 * @property {boolean} [active]
 * @property {boolean} [noFurtherInstructions]
 */

/**
 * @typedef {Object} FirmSummaryPage
 * @property {FirmSummary[]} items
 * @property {number} page
 * @property {number} pageSize
 * @property {number} total
 */

/**
 * A loan this firm is currently assigned to as a party on that loan's Professional Details
 * (DF169-177) — one row per (loan, role) pair, so a firm playing two different roles on the
 * same loan appears twice, once per role (v1.5.0/v1.6.0). Reverse lookup of
 * LoanProfessionalDetails' PartyAssignments — not data stored on the firm.
 * @typedef {Object} AssociatedLoan
 * @property {string} [loanId] internal identifier for GET /loans/{loanId}/professional-details — NEVER render this; see loanNumber
 * @property {string} [loanNumber] the loan's human-readable account reference — always what's displayed (v1.6.0)
 * @property {string} [loanName]
 * @property {string} [borrowerName]
 * @property {number} [facilityLimit]
 * @property {string} [currency] default "GBP"
 * @property {string} [status] e.g. Live/Redeemed/Distressed — not a contract enum, owned by the core Loan domain
 * @property {string} [stage] e.g. Enquiry/Credit Review/Due Diligence/Completion/In Life/Loan Closed — not a contract enum
 * @property {LoanPartyRole} [role]
 * @property {string} [roleLabel] matches LOAN_DETAIL_ROLE_LABELS (v1.4.0 rename)
 * @property {?string} [assignedDate] ISO date; null for roles without lifecycle dates (broker/borrowerSolicitor/administrator/receiver)
 */

/**
 * @typedef {Object} AssociatedLoanPage
 * @property {AssociatedLoan[]} items
 * @property {number} page
 * @property {number} pageSize
 * @property {number} total
 */

/**
 * The result of GET /professional-firms/name-available (v1.13.0) — is this company name free,
 * and if not, which firm holds it. `existingFirmId` lets the warning link through to that firm
 * rather than only naming it; `existingFirmName` is the name in its STORED casing, so the UI
 * shows the name as it actually sits in the repository rather than echoing what was typed,
 * which is the whole point when the only difference is case. Both are null when available.
 *
 * v1.14.0 (FIRM-DUPLICATE-NAMES-010/-012, R12): the answer is scoped to the `type` that was
 * asked about (now a required query param on the endpoint) — this shape is unchanged, but it
 * no longer means "free/taken anywhere in the repository", only "free/taken for that type".
 * @typedef {Object} FirmNameCheck
 * @property {boolean} available
 * @property {?string} [existingFirmId]
 * @property {?string} [existingFirmName]
 */

/**
 * @typedef {Object} BulkResultRow
 * @property {number} rowNumber
 * @property {string} companyName
 * @property {"Ready"|"Warning"|"Error"|"Duplicate"} status v1.13.0 adds "Duplicate": a VALID row whose company name already exists, skipped by the import and needing no action from the user — deliberately not "Error", which means a row the user must fix
 * @property {string} [message]
 * @property {?string} [existingFirmId] v1.13.0 — the repository firm already holding the name. Set only on "Duplicate" rows matched against the repository; null otherwise, including a row duplicating an earlier row of the same file (that firm does not exist yet when the row is judged)
 */

/**
 * @typedef {Object} BulkResult
 * @property {number} totalRows
 * @property {number} readyCount
 * @property {number} warningCount
 * @property {number} errorCount
 * @property {number} [duplicateCount] v1.13.0 — rows skipped as already existing. readyCount + warningCount + errorCount + duplicateCount === totalRows
 * @property {BulkResultRow[]} rows
 */

/**
 * A role on a loan filled by a repository firm plus the SELECTED active individual and
 * address to display on the loan (DF174-175), plus lifecycle dates.
 * @typedef {Object} PartyAssignment
 * @property {?string} [firmId]
 * @property {?string} [contactId]
 * @property {?string} [addressId]
 * @property {Object.<string, ?string>} [dates] ISO date values, keyed per role (see LOAN_ROLE_DATE_FIELDS)
 */

/**
 * A quote's supporting document (scanned quote / PDF / spreadsheet / image) filed under Loan
 * Documents in the fixed, system-generated "Quotes" category (never user-editable — see
 * QUOTE_DOCUMENT_CATEGORY_NAME in professionalContacts.rules.js). Not a contract entity of its
 * own — it travels embedded on the Quote it belongs to. See
 * docs/planning/professional-contacts-quote-documents/00-overview.md for the full contract.
 * @typedef {Object} QuoteDocument
 * @property {string} fileName original file name as selected by the user, e.g. "valuer-quote.pdf"
 * @property {string} filePath the Document Helper storage reference the UI passes back for
 *   download — "<loanId>/Quotes/<fileName>" — NOT a signed URL (DF.TblFileCategory-backed path)
 * @property {string} [fileExtension] lower-cased, including the dot, e.g. ".pdf"
 * @property {string} [contentType] browser-reported MIME type at upload time, e.g. "application/pdf"
 * @property {string} categoryName always "Quotes" — system-controlled, never user-editable
 * @property {string} [uploadedUtc] ISO date-time the document was uploaded
 */

/**
 * @typedef {Object} Quote
 * @property {LoanPartyRole} [role]
 * @property {string} [firmId]
 * @property {string} [firmName]
 * @property {number} [value]
 * @property {string} [currency] default "GBP"
 * @property {?QuoteDocument} [document] supporting document for this quote (optional)
 */

/**
 * @typedef {Object} LoanProfessionalDetails
 * @property {string} [loanId]
 * @property {PartyAssignment} [bankSolicitor]
 * @property {PartyAssignment} [bankValuer]
 * @property {PartyAssignment} [monitoringSurveyor]
 * @property {PartyAssignment} [broker]
 * @property {PartyAssignment} [borrowerSolicitor]
 * @property {PartyAssignment} [administrator]
 * @property {PartyAssignment} [receiver] wired up in v1.4.0 — mirrors administrator (no lifecycle dates)
 * @property {Quote[]} [quotes] max 3 per role (Valuer/Surveyor/Solicitor)
 * @property {?string} [notes] v1.9.0: Summary Notes free text; null on a loan that has never had a note saved. Who/when last saved it is in the audit trail (AuditEntry.entity='Loan'), not on this field.
 */

/**
 * A loan's Professional Details role that currently has a firm assigned, shaped for the
 * Outbound Payments "Third Party" picker (Step A, v1.3.0). Display-only context — not itself
 * the source of the fields an Outbound Payment locks (see MainBankAccount for that).
 * @typedef {Object} PayableParty
 * @property {string} [roleKey] one of LOAN_DETAIL_ROLE_KEYS' values
 * @property {string} [roleLabel]
 * @property {string} [firmId]
 * @property {string} [firmName]
 * @property {string} [mainIndividualName]
 * @property {?string} [mainBankAccountNumberMasked] e.g. "•••• 4321"; null if the firm has no bank account yet
 */

/**
 * The 3 fields an Outbound Payment has always locked for Borrower, sourced instead from a
 * firm's isMain BankAccount (Step B, v1.3.0).
 * @typedef {Object} MainBankAccount
 * @property {string} [bankAccountId]
 * @property {string} [beneficiaryName]
 * @property {string} [sortCode]
 * @property {string} [accountNumber]
 */

/**
 * @typedef {Object} AuditEntry
 * @property {string} [id]
 * @property {string} [timestampUtc]
 * @property {string} [user]
 * @property {"Firm"|"Contact"|"Address"|"Loan"} [entity]
 * @property {string} [entityId]
 * @property {"Insert"|"Update"|"Delete"} [action]
 * @property {string} [detail]
 */

/**
 * @typedef {Object} ApiFieldError
 * @property {string} [field]
 * @property {string} [message]
 */

/**
 * @typedef {Object} ApiError
 * @property {string} [code]
 * @property {string} [message]
 * @property {ApiFieldError[]} [fieldErrors]
 */

/** The seven repository firm types, in contract order. */
export const FIRM_TYPES = [
  "Solicitor",
  "BorrowerSolicitor",
  "Broker",
  "Valuer",
  "Surveyor",
  "Administrator",
  "Receiver",
];

/** The seven loan party roles a firm can be assigned to, in contract order. */
export const LOAN_PARTY_ROLES = [
  "BankSolicitor",
  "BankValuer",
  "MonitoringSurveyor",
  "Broker",
  "BorrowerSolicitor",
  "Administrator",
  "Receiver",
];

/** Firm types that are treated as "distressed" scenarios by default in the prototype. */
export const DISTRESSED_FIRM_TYPES = ["Administrator", "Receiver"];

/**
 * Human labels for each FirmType (display label only, v1.4.0 — the FirmType enum value itself
 * is unchanged, see FIRM_TYPES). Solicitor and BorrowerSolicitor are renamed to
 * "Solicitor_Lender"/"Solicitor_Borrower" to disambiguate which side of a transaction the firm
 * acts for; the other five keep their plain enum value as the label. Single source of truth —
 * every screen that shows a FirmType (repository tabs, the firm record, the add/edit form)
 * reads through this rather than rendering the raw enum value.
 */
export const FIRM_TYPE_LABELS = {
  Solicitor: "Solicitor_Lender",
  BorrowerSolicitor: "Solicitor_Borrower",
  Broker: "Broker",
  Valuer: "Valuer",
  Surveyor: "Surveyor",
  Administrator: "Administrator",
  Receiver: "Receiver",
};

/** @param {string} type a FirmType */
export function firmTypeLabel(type) {
  return FIRM_TYPE_LABELS[type] || type;
}

// Roles surfaced on the loan Professional Details screen/editor (DF169-177). "Receiver" was
// part of the contract's LoanPartyRole enum from v1.2 but deliberately left out of these seven
// — wired up in v1.4.0 as a repository-backed role mirroring Administrator exactly (no
// lifecycle dates), per the "Receiver linked with Professional Details" request.
export const LOAN_DETAIL_ROLE_KEYS = {
  BankSolicitor: "bankSolicitor",
  BankValuer: "bankValuer",
  MonitoringSurveyor: "monitoringSurveyor",
  Broker: "broker",
  BorrowerSolicitor: "borrowerSolicitor",
  Administrator: "administrator",
  Receiver: "receiver",
};

/**
 * Human labels for each Professional Details role key, matching the prototype's client layout.
 * Renamed in v1.4.0 (display label only — the underlying LoanPartyRole/roleKey values are
 * unchanged, see LOAN_DETAIL_ROLE_KEYS/LOAN_DETAIL_ROLE_TO_PARTY_ROLE): bankSolicitor and
 * borrowerSolicitor now read "Solicitor_Lender"/"Solicitor_Borrower" (matching the renamed
 * FirmType labels below), bankValuer drops the "Bank's" qualifier to just "Valuer", and
 * monitoringSurveyor reads "Monitoring Surveyor".
 */
export const LOAN_DETAIL_ROLE_LABELS = {
  bankSolicitor: "Solicitor_Lender",
  bankValuer: "Valuer",
  monitoringSurveyor: "Monitoring Surveyor",
  broker: "Broker",
  borrowerSolicitor: "Solicitor_Borrower",
  administrator: "Loan administration",
  receiver: "Receiver",
};

/** FirmType each Professional Details role picker searches against (drives /selectable-firms). */
export const LOAN_DETAIL_ROLE_FIRM_TYPE = {
  bankSolicitor: "Solicitor",
  bankValuer: "Valuer",
  monitoringSurveyor: "Surveyor",
  broker: "Broker",
  borrowerSolicitor: "BorrowerSolicitor",
  administrator: "Administrator",
  receiver: "Receiver",
};

/**
 * LoanPartyRole each Professional Details role key maps to — the inverse of
 * LOAN_DETAIL_ROLE_KEYS. This (NOT LOAN_DETAIL_ROLE_FIRM_TYPE) is what GET
 * /loans/{loanId}/professional-details/selectable-firms?role= expects: the contract's `role`
 * param is a LoanPartyRole, not a FirmType. They share the same string for Broker/
 * BorrowerSolicitor/Administrator, which masked the mismatch for those three roles, but
 * Solicitor/Valuer/Surveyor differ from BankSolicitor/BankValuer/MonitoringSurveyor and the
 * API 400s with "Unknown loan party role" if a FirmType is sent instead.
 */
export const LOAN_DETAIL_ROLE_TO_PARTY_ROLE = Object.fromEntries(
  Object.entries(LOAN_DETAIL_ROLE_KEYS).map(([partyRole, roleKey]) => [roleKey, partyRole])
);

/** Roles that are mandatory per the prototype's "Edit Loan" completion chip (§7 flags the rest as TODO). */
export const LOAN_DETAIL_REQUIRED_ROLES = ["bankSolicitor", "bankValuer", "borrowerSolicitor"];

/** date-field keys per role, per PartyAssignment.dates' description in the contract. */
export const LOAN_ROLE_DATE_FIELDS = {
  bankSolicitor: [
    { key: "instructed", label: "Date solicitor instructed" },
    { key: "reportOnTitleReceived", label: "Date report on title received" },
    { key: "chargeConfirmedEmailFiled", label: "Date charge confirmed & email filed" },
  ],
  bankValuer: [
    { key: "instructed", label: "Date valuer instructed" },
    { key: "originalValuationReport", label: "Date Initial Report Received" },
    { key: "latestValuationReport", label: "Date latest valuation report" },
  ],
  monitoringSurveyor: [
    { key: "instructed", label: "Date PMS instructed" },
    { key: "reportReceived", label: "Date PMS report received" },
  ],
  broker: [],
  borrowerSolicitor: [],
  administrator: [],
  receiver: [],
};

/** Quote "type" groups per the prototype/§4b quotes editor (a subset of FirmType). */
export const QUOTE_TYPES = ["Valuer", "Surveyor", "Solicitor"];

/** Maps a quotes-editor "type" to the LoanPartyRole a Quote.role actually carries on the wire. */
export const QUOTE_TYPE_TO_LOAN_ROLE = {
  Valuer: "BankValuer",
  Surveyor: "MonitoringSurveyor",
  Solicitor: "BankSolicitor",
};

/**
 * Columns the Associated Loans view can sort by (v1.5.0/v1.6.0) — matches the contract's
 * GET /professional-firms/{firmId}/loans `sort` enum exactly. Single source of truth so the
 * dialog's sortable table headers and the query-building helper can't drift apart.
 */
export const LOAN_ASSOCIATED_LOANS_SORT_FIELDS = ["loanName", "borrowerName", "facilityLimit", "status", "stage"];

export {};
